// Onboarding tab -- the data side. Everything goes through the onboarding
// migration's staff RPCs and its allowlisted RLS (2026-10-02-onboarding.sql):
//   onboarding_start / _revoke / _secret_status / _decrypt_log / _adp_view /
//   _mark_entered_in_adp, plus plain selects on onboarding_sessions (never the
//   token column), onboarding_pages, onboarding_documents, onboarding_expirations.
// A login that is not on the staff allowlist gets 42501 / no rows: the tab
// shows it disabled. Decrypted values are returned to the caller and never
// stored here.

import { makeExamsApi, ATTEMPT_COLS, isMissingTable } from './exams';

// The portal's checklist order (its CONFIRMED_PAGES), the I-9 included since Stage 3.
export const PAGE_ORDER = ['personal', 'emergency', 'tax_home', 'w4', 'i9', 'direct_deposit', 'documents', 'background'];
export const PAGE_LABELS = {
  personal: 'About you', emergency: 'Emergency contacts', tax_home: 'Tax home', w4: 'Tax withholding (W-4)', i9: 'I-9 (Section 1)',
  direct_deposit: 'Direct deposit', documents: 'Documents', background: 'Background check',
};
export const DOC_LABELS = {
  license: 'License', cpr: 'CPR card', immunization: 'Immunizations', gov_id_front: 'Government ID (front)',
  gov_id_back: 'Government ID (back)', resume: 'Resume', voided_check: 'Voided check', i20: 'I-20',
  background_authorization: 'Signed background authorization', tb_test: 'TB test result', physical: 'Physical',
};

// DAY-DIRECTSTAFF-2026-10-22 (Matt's ruling): the portal's Health section -- a TB test result and a physical, "if your
// facility requires it". Staff tick "required" per candidate (onboarding_health_set, logged on the timeline); only a ticked
// upload counts in the candidate's progress, is asked for, or is missing here. Dark until the hub applies
// 2026-10-22-onboarding-health.sql (the table not there -> null, and nothing shows).
export const HEALTH_KINDS = ['tb_test', 'physical'];
export const HEALTH_UPLOAD_TEXT = { tb_test: 'Upload TB test result', physical: 'Upload physical' };
const missingTable = (e) => !!e && (/PGRST205|42P01/.test(String(e.code || '')) || /does not exist|could not find the table/i.test(String(e.message || '')));
/** -> { tb_test, physical, set_by, set_at } (both false when never set); null = not switched on; undefined = not readable */
export async function readHealthRequired(supabase, candidateId) {
  try {
    const r = await supabase.from('onboarding_health_required').select('tb_test,physical,set_by,set_at').eq('candidate_id', candidateId).limit(1);
    if (r.error) return missingTable(r.error) ? null : undefined;
    return (r.data || [])[0] || { tb_test: false, physical: false, set_by: null, set_at: null };
  } catch (e) {
    return undefined;
  }
}
/** The health uploads this candidate is required to send and has not: kinds, in order. */
export const healthMissingFor = (health, documents) => (health
  ? HEALTH_KINDS.filter((k) => health[k] === true && !(documents || []).some((d) => d.kind === k)) : []);
export const RESTRICTED = 'Onboarding is restricted to allowlisted staff.';
export const RED_DAYS = 30;

const SESSION_COLS = 'id,candidate_id,assignment_id,status,created_by,created_at,expires_at,completed_at,revoked_at,revoked_by';
const isDenied = (error) => !!error && (error.code === '42501' || /not_on_staff_allowlist|staff_only|permission denied/.test(error.message || ''));

export function effectiveStatus(s, now = Date.now()) {
  if (!s) return 'none';
  if (['pending', 'in_progress'].includes(s.status) && new Date(s.expires_at).getTime() < now) return 'expired';
  return s.status;
}

export function daysLeft(dateStr, now = new Date()) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00Z`).getTime();
  const t = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((d - t) / 864e5);
}
export const isRed = (days) => days !== null && days <= RED_DAYS;

// hub 2026-10-08: the newest onboarding nudge for the candidate (agent_drafts, source onboarding_nudge), for the
// "Last nudge" line. null = none yet; undefined = it could not be read -- the line says so and the tab still loads.
export const NUDGE_COLS = 'id,status,created_at,approved_at,sent_at,expires_at,check_passed';
export async function readLastNudge(supabase, candidateId) {
  try {
    const r = await supabase.from('agent_drafts').select(NUDGE_COLS).eq('candidate_id', candidateId)
      .eq('source', 'onboarding_nudge').order('created_at', { ascending: false }).limit(1);
    if (r.error) return undefined;
    return (r.data || [])[0] || null;
  } catch (e) {
    return undefined;
  }
}

// Hub 2026-10-12: what the newest link's background record says beside the background-check row -- the "I have no
// middle name" tick (migrations/2026-10-12-background-middle-name.sql), and whether a SIGNED record lacks the degree
// details an education verification needs (migrations/2026-10-12-background-degree.sql; signed before they were asked).
// -> { noMiddleName, degreeMissing, signedAt, sentToSterlingAt, sentToSterlingBy }, or undefined when it cannot be read --
// the tab still loads, nothing is shown. CRM-ONBOARDING-TAB-2026-10-13: the Sterling mark
// (migrations/2026-10-13-onboarding-overview.sql) and the signature time, for "Mark sent to Sterling".
export async function readBackgroundStatus(supabase, sessionId) {
  try {
    const r = await supabase.from('background_authorizations')
      .select('no_middle_name,signed_at,degree_saved_at,sent_to_sterling_at,sent_to_sterling_by').eq('session_id', sessionId);
    if (r.error) return undefined;
    const b = (r.data || [])[0] || {};
    return { noMiddleName: b.no_middle_name === true, degreeMissing: !!b.signed_at && !b.degree_saved_at, signedAt: b.signed_at || null,
      sentToSterlingAt: b.sent_to_sterling_at || null, sentToSterlingBy: b.sent_to_sterling_by || null };
  } catch (e) {
    return undefined;
  }
}
// 2026-10-14 (candidate 78, session 8): what the newest link still needs, item by item, in the portal's checklist order
// and words -- a form unsigned, a page unconfirmed, the background authorization unsigned. -> [string]; [] when nothing
// is left (or there is no link); null when it can't be read. (The CRM does not see the answers themselves, so a form
// shown as "Sign" may need answers first; the portal's own list says which.)
const SIGN_TEXT = {
  emergency: 'Sign the Emergency Contact form', tax_home: 'Sign the Tax Home questionnaire', w4: 'Sign the W-4',
  i9: 'Sign the I-9', direct_deposit: 'Sign the Direct Deposit form',
};
export const BACKGROUND_STILL_TEXT = 'Add the degree and sign the background check authorization';
export function stillNeededFor(data) {
  if (!data || !data.allowed || data.signedForms === undefined) return null;
  const latest = data.latest;
  if (!latest || latest.completed_at) return [];
  const attested = new Set((data.pages || []).filter((p) => p.attested_at).map((p) => p.page));
  const signed = data.signedForms || {};
  const out = [];
  for (const key of PAGE_ORDER) {
    if (key === 'background') for (const k of healthMissingFor(data.health, data.documents)) out.push(HEALTH_UPLOAD_TEXT[k]);   // 10-22
    if (key === 'background') { if (!signed.background) out.push(BACKGROUND_STILL_TEXT); }
    else if (SIGN_TEXT[key] && !signed[key]) out.push(SIGN_TEXT[key]);
    else if (!attested.has(key)) out.push(`Confirm ${PAGE_LABELS[key]}`);
  }
  return out;
}
export const NO_MIDDLE_NAME_TEXT = 'No middle name (candidate attested)';
export const DEGREE_MISSING_TEXT = 'Degree details missing';
// CRM-ONBOARDING-TAB-2026-10-13: when each of the newest link's forms was signed (times only, never the signature text).
// -> { <form>: signed_at }, or undefined when it cannot be read.
export async function readFormSignatures(supabase, sessionId) {
  try {
    const r = await supabase.from('onboarding_form_signatures').select('form,signed_at').eq('session_id', sessionId);
    if (r.error) return undefined;
    return Object.fromEntries((r.data || []).filter((x) => x.signed_at).map((x) => [x.form, x.signed_at]));
  } catch (e) {
    return undefined;
  }
}
export const DEGREE_COLS = 'degree,degree_major,degree_graduated,degree_school,degree_school_city_state,degree_last_name';

export function onboardLink(token, base) {
  return `${String(base || '').replace(/\/+$/, '')}/t/${token}`;
}

async function latestSession(supabase, candidateId) {
  const r = await supabase.from('onboarding_sessions').select(SESSION_COLS).eq('candidate_id', candidateId)
    .order('created_at', { ascending: false }).limit(1);
  if (r.error) throw new Error(r.error.message);
  return (r.data || [])[0] || null;
}

// OVERNIGHT-DIRECTSTAFF-2026-10-10 §4: "Entered in ADP" erases the SSN and bank numbers, after which the forms print
// without them. A FULL packet download is logged with exactly PACKET_NOTE (a one-form download says which form), and
// the guard asks for a second click when no full download is on the candidate's activity -- logging that override.
export const PACKET_LOG_TYPE = 'onboarding_packet';
export const PACKET_NOTE = 'Onboarding packet downloaded';
export const ADP_OVERRIDE_LOG_TYPE = 'onboarding_adp_override';
// Hub ruling 2026-10-14: exactly what "Entered in ADP" erases (migrations/2026-10-14-adp-erase-i9-numbers.sql for the
// numbers; deleteVoidedChecks for the image) -- one sentence everywhere the button is explained.
export const ERASE_TEXT = 'the SSN, every routing and account number, the USCIS/A-number, the I-94 number, the passport number, '
  + 'and the uploaded voided check image(s)';
export const PACKET_KEEP_TEXT = 'Keep this packet in your secure HR file; delete any other copies.';
const packetNote = (only, by) => `${PACKET_NOTE}${only ? ` (${only} only)` : ''}${by ? ` by ${by}` : ''}`;
/** A logged note for a FULL packet download (not a one-form one). */
export const isFullPacketNote = (note) => {
  const n = String(note || '');
  return n === PACKET_NOTE || n.startsWith(`${PACKET_NOTE} by `);
};

// An activity_log row (the candidate's Activity tab): what was opened, by whom -- never a value.
async function logView(supabase, candidateId, type, note) {
  const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId, date: new Date().toISOString().slice(0, 10), type, note });
  if (error) throw new Error(`Could not log this access, so nothing was shown: ${error.message}`);
}

// ONBOARD-EXAMS-v2-2026-10-16: a candidate's passed exams, each with its stored certificate re-hashed now ([] before the
// exams migration, or when there are none).
export async function passedExams(supabase, candidateId) {
  const { data, error } = await supabase.from('onboarding_exam_attempts').select(ATTEMPT_COLS)
    .eq('candidate_id', candidateId).eq('passed', true).order('submitted_at', { ascending: true });
  if (error) { if (isMissingTable(error)) return []; throw new Error(error.message); }
  const api = makeExamsApi(supabase);
  const out = [];
  for (const a of data || []) out.push({ ...a, current_sha256: a.cert_path ? await api.certificateSha256(a.cert_path) : null });
  return out;
}

export function makeOnboardingApi(supabase) {
  const rpc = async (fn, args) => {
    const { data, error } = await supabase.rpc(fn, args);
    if (error) { const e = new Error(error.message); e.code = error.code; e.denied = isDenied(error); throw e; }
    return data;
  };
  return {
    // -> { allowed:false } or the tab's whole picture for one candidate
    async load(candidateId) {
      const status = await supabase.rpc('onboarding_secret_status', { p_candidate_id: candidateId });
      if (status.error) {
        if (isDenied(status.error)) return { allowed: false };
        throw new Error(status.error.message);
      }
      const [sessions, docs, log, expirations, assignments] = await Promise.all([
        supabase.from('onboarding_sessions').select(SESSION_COLS).eq('candidate_id', candidateId).order('created_at', { ascending: false }),
        supabase.from('onboarding_documents').select('id,kind,license_state,expires_on,uploaded_at,storage_path').eq('candidate_id', candidateId).order('uploaded_at', { ascending: false }),
        supabase.rpc('onboarding_decrypt_log', { p_candidate_id: candidateId }),
        supabase.from('onboarding_expirations').select('kind,license_state,expires_on,days_left').eq('candidate_id', candidateId),
        supabase.from('assignments').select('id,facility,start_date,end_date,facility_documents').eq('candidate_id', candidateId).order('id', { ascending: false }),
      ]);
      for (const r of [sessions, docs, log, expirations, assignments]) if (r.error) throw new Error(r.error.message);
      const latest = sessions.data[0] || null;
      let pages = [];
      if (latest) {
        const p = await supabase.from('onboarding_pages').select('page,attested_at,updated_at,data').eq('session_id', latest.id);
        if (p.error) throw new Error(p.error.message);
        pages = p.data;
      }
      const personal = pages.find((p) => p.page === 'personal');
      const lastNudge = await readLastNudge(supabase, candidateId);
      const bg = latest ? await readBackgroundStatus(supabase, latest.id) : { noMiddleName: false, degreeMissing: false };
      const signed = latest ? await readFormSignatures(supabase, latest.id) : {};
      const health = await readHealthRequired(supabase, candidateId);   // DAY-DIRECTSTAFF-2026-10-22
      return {
        allowed: true,
        sessions: sessions.data,
        latest,
        pages: pages.map(({ page, attested_at, updated_at }) => ({ page, attested_at, updated_at })),
        legalName: (personal && personal.data && personal.data.legal_name) || null,
        onFile: status.data,
        documents: docs.data,
        decryptLog: log.data || [],
        expirations: expirations.data,
        assignments: assignments.data,
        lastNudge,
        noMiddleName: bg ? bg.noMiddleName : undefined,
        degreeMissing: bg ? bg.degreeMissing : undefined,
        // when each form was signed (the background authorization included); undefined = could not be read
        signedForms: signed && bg ? { ...signed, ...(bg.signedAt ? { background: bg.signedAt } : {}) } : undefined,
        sterling: bg ? { at: bg.sentToSterlingAt || null, by: bg.sentToSterlingBy || null } : undefined,
        health,
      };
    },
    // DAY-DIRECTSTAFF-2026-10-22: what this candidate's facility requires (both boxes each time; logged by the function)
    setHealth: (candidateId, tbTest, physical) => rpc('onboarding_health_set', { p_candidate_id: candidateId, p_tb_test: tbTest === true, p_physical: physical === true }),
    // PORTAL-PACKET-2026-10-09 §7: everything the candidate entered (not the encrypted SSN / bank numbers -- those stay
    // behind "Ready for ADP"), from the newest link. Every read is logged on the candidate's activity, like the rest.
    async details(candidateId, by) {
      const latest = await latestSession(supabase, candidateId);
      if (!latest) return null;
      const [pages, tax, bg] = await Promise.all([
        supabase.from('onboarding_pages').select('page,data,attested_at').eq('session_id', latest.id),
        supabase.from('onboarding_tax_home').select('data').eq('session_id', latest.id).maybeSingle(),
        // PORTAL-DEGREE-2026-10-12: the degree details on the background record
        supabase.from('background_authorizations').select(DEGREE_COLS).eq('session_id', latest.id).maybeSingle(),
      ]);
      for (const r of [pages, tax, bg]) if (r.error) throw new Error(r.error.message);
      await logView(supabase, candidateId, 'onboarding_view', `Onboarding details viewed${by ? ` by ${by}` : ''}`);
      const byPage = Object.fromEntries((pages.data || []).map((p) => [p.page, p.data || {}]));
      return { personal: byPage.personal || {}, emergency: byPage.emergency || {}, w4: byPage.w4 || {},
        directDeposit: byPage.direct_deposit || {}, i9: byPage.i9 || {}, taxHome: (tax.data && tax.data.data) || {}, background: bg.data || {} };
    },
    // §6: what the packet is built from. The SSN and bank numbers come through onboarding_adp_view -- the same logged
    // decrypt as "Ready for ADP" -- and the download itself is logged too.
    async packetData(candidate, by, only = null) {
      const latest = await latestSession(supabase, candidate.id);
      if (!latest) throw new Error('This candidate has no onboarding link yet.');
      const [pages, tax, bg, docs, sigs] = await Promise.all([
        supabase.from('onboarding_pages').select('page,data,attested_at,attest_ip').eq('session_id', latest.id),
        supabase.from('onboarding_tax_home').select('data').eq('session_id', latest.id).maybeSingle(),
        supabase.from('background_authorizations').select(`full_name,aliases,signature,consented,signed_at,signed_ip,disclosure_viewed_at,${DEGREE_COLS}`)
          .eq('session_id', latest.id).maybeSingle(),
        supabase.from('onboarding_documents').select('kind,storage_path,uploaded_at').eq('candidate_id', candidate.id).order('uploaded_at', { ascending: false }),
        // each form's own signature (migrations/2026-10-09-onboarding-forms.sql)
        supabase.from('onboarding_form_signatures').select('form,signature,consented,signed_at,signed_ip').eq('session_id', latest.id),
      ]);
      for (const r of [pages, tax, bg, docs, sigs]) if (r.error) throw new Error(r.error.message);
      const secrets = await rpc('onboarding_adp_view', { p_candidate_id: candidate.id });
      // PORTAL-ESIGN-PROOF-2026-10-12: the signature certificate -- consent, each signature's evidence, the content hash
      // stored at signing and recomputed now (allowlisted staff; hashes only)
      const certificate = await rpc('onboarding_signature_certificate', { p_candidate_id: candidate.id });
      await logView(supabase, candidate.id, PACKET_LOG_TYPE, packetNote(only, by));
      return {
        candidate: { id: candidate.id, name: candidate.name, email: candidate.email, phone: candidate.phone },
        pages: Object.fromEntries((pages.data || []).map((p) => [p.page, p])),
        taxHome: (tax.data && tax.data.data) || {},
        background: bg.data || {},
        documents: docs.data || [],
        signatures: sigs.data || [],
        secrets: secrets || {},
        // ONBOARD-EXAMS-v2-2026-10-16: passed exams join the certificate page (their certificate's SHA-256 stored at issue,
        // re-hashed now from the stored file). Never required: no exams, or no exams table yet, adds nothing.
        certificate: certificate ? { ...certificate, exams: await passedExams(supabase, candidate.id) } : null,
        sessionId: latest.id,
      };
    },
    // CRM-ONBOARDING-TAB-2026-10-13 (migrations/2026-10-13-onboarding-overview.sql): one row per link, for the tab;
    // -> { allowed:false } or { allowed:true, rows }
    async overview() {
      const { data, error } = await supabase.rpc('onboarding_overview');
      if (error) {
        if (isDenied(error)) return { allowed: false };
        throw new Error(error.message);
      }
      return { allowed: true, rows: data || [] };
    },
    extendLink: (sessionId) => rpc('onboarding_extend_link', { p_session_id: sessionId }),
    // ONBOARD-RELINK-2026-10-15 (migrations/2026-10-15-onboarding-relink.sql): a new token for the SAME session -- all
    // progress kept, the previous link dead. -> { session_id, token, expires_at }; the token is shown once, never stored.
    relink: (sessionId) => rpc('onboarding_relink', { p_session_id: sessionId }),
    markSentToSterling: (sessionId) => rpc('onboarding_mark_sent_to_sterling', { p_session_id: sessionId }),
    start: (candidateId) => rpc('onboarding_start', { p_candidate_id: candidateId }),
    revoke: (sessionId) => rpc('onboarding_revoke', { p_session_id: sessionId }),
    adpView: (candidateId) => rpc('onboarding_adp_view', { p_candidate_id: candidateId }),
    markEnteredInAdp: (candidateId) => rpc('onboarding_mark_entered_in_adp', { p_candidate_id: candidateId }),
    // Hub ruling 2026-10-14: "Entered in ADP" also deletes the candidate's uploaded voided check image(s) -- the stored file
    // (Storage API; the database can't delete stored objects) and then its document row, logged with the count only.
    // -> how many were deleted. Throws a sentence on any failure, leaving the rows so the delete can be retried.
    async deleteVoidedChecks(candidateId) {
      const { data, error } = await supabase.from('onboarding_documents').select('id,storage_path')
        .eq('candidate_id', candidateId).eq('kind', 'voided_check');
      if (error) throw new Error(`The voided check image could not be found to delete: ${error.message}`);
      const rows = data || [];
      if (!rows.length) return 0;
      const rm = await supabase.storage.from('onboarding-docs').remove(rows.map((r) => r.storage_path));
      if (rm.error) throw new Error(`The voided check image could not be deleted: ${rm.error.message}`);
      const del = await supabase.from('onboarding_documents').delete().in('id', rows.map((r) => r.id));
      if (del.error) throw new Error(`The voided check image was deleted, but its record could not be: ${del.error.message}`);
      await logView(supabase, candidateId, 'onboarding', `Voided check image(s) deleted after "Entered in ADP": ${rows.length}`);
      return rows.length;
    },
    // §4: true when a FULL packet download is logged for the candidate; false when none is; null when it can't be read
    // (the guard then asks for the second click, as for none).
    async packetDownloaded(candidateId) {
      const { data, error } = await supabase.from('activity_log').select('id,note').eq('candidate_id', candidateId)
        .eq('type', PACKET_LOG_TYPE);
      if (error) return null;
      return (data || []).some((r) => isFullPacketNote(r.note));
    },
    // §4: the second click, logged BEFORE the erase; a failed log refuses the erase.
    async logAdpOverride(candidateId, by, unread = false) {
      await logView(supabase, candidateId, ADP_OVERRIDE_LOG_TYPE,
        `"Entered in ADP" confirmed with ${unread ? 'the packet download history unreadable' : 'no full packet download on file'}${by ? ` by ${by}` : ''}`);
    },
    async signedUrl(path) {
      const { data, error } = await supabase.storage.from('onboarding-docs').createSignedUrl(path, 60);
      if (error) throw new Error(error.message);
      return data.signedUrl;
    },
    // The facility-documents slot: one file into the private bucket, appended to
    // assignments.facility_documents. Nothing more (no facility-forms module).
    async addFacilityDocument(assignment, candidateId, file, by) {
      const safe = String(file.name || 'document').replace(/[^A-Za-z0-9._-]+/g, '_').slice(-80);
      const path = `${candidateId}/facility/${assignment.id}/${Date.now()}-${safe}`;
      const up = await supabase.storage.from('onboarding-docs').upload(path, file, { upsert: false, contentType: file.type || undefined });
      if (up.error) throw new Error(up.error.message);
      const next = [...(assignment.facility_documents || []), { path, name: safe, uploaded_at: new Date().toISOString(), uploaded_by: by || null }];
      const { error } = await supabase.from('assignments').update({ facility_documents: next, updated_at: new Date().toISOString() }).eq('id', assignment.id);
      if (error) throw new Error(error.message);
      return next;
    },
  };
}

// ---------------------------------------------------------------- the email
// Config comes from the build environment (REACT_APP_*), never typed here.
export function onboardingConfig(env = process.env) {
  const cfg = {
    companyName: String(env.REACT_APP_COMPANY_NAME || '').trim(),
    businessLine: String(env.REACT_APP_BUSINESS_LINE || '').trim(),
    baseUrl: String(env.REACT_APP_ONBOARD_BASE_URL || '').trim(),
  };
  cfg.missing = Object.entries({ REACT_APP_COMPANY_NAME: cfg.companyName, REACT_APP_BUSINESS_LINE: cfg.businessLine,
    REACT_APP_ONBOARD_BASE_URL: cfg.baseUrl }).filter(([, v]) => !v).map(([k]) => k);
  return cfg;
}

// Hub ruling 2026-10-03 (d): the first word of the name as stored; when the
// name can't be split into words, the name as stored.
export function firstNameOf(name) {
  const stored = String(name || '').trim();
  const words = stored.split(/\s+/).filter(Boolean);
  if (words.length < 2) return stored;
  return words[0].replace(/[,;:]+$/, '') || stored;
}

export function buildOnboardingEmail({ firstName, link, expiresAt, config }) {
  if (config.missing && config.missing.length) throw new Error(`onboarding email config missing: ${config.missing.join(', ')}`);
  if (!/^https:\/\//.test(link)) throw new Error('onboarding email needs an https link');
  const until = new Date(expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const hello = firstName ? `Hi ${firstName},` : 'Hi,';
  const subject = `Your ${config.companyName} onboarding checklist`;
  const body = [
    hello,
    '',
    `Here is your personal link to the ${config.companyName} onboarding checklist:`,
    link,
    '',
    'It works on your phone, and every answer saves as you go, so you can stop and come back with the same link.',
    '',
    'Have these ready:',
    '- Your professional license (the state and expiration date)',
    '- Your CPR card',
    '- Your immunization records',
    '- A government ID (front and back)',
    '- Your resume',
    '- Your bank routing and account numbers, and a voided check or bank letter',
    '- Your Social Security number for the W-4',
    'We only ever collect your Social Security number and bank details on the checklist at the link above — never by email or text.',
    '',
    `The link expires on ${until}. This link is yours alone — please don't forward it.`,
    '',
    `Questions? Call or text us at ${config.businessLine}, or reply to this email.`,
    '',
    config.companyName,
  ].join('\n');
  return { subject, body };
}
